"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Clock, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Phenolicengravingcapabilities() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const workshops = [{
    date: "Industrial Grade",
    title: "Phenolic Legend Plates",
    description: "Precision-engraved, multi-layer phenolic plates designed for harsh environments. Resistant to chemicals, heat, and UV exposure. Built for long-term identification.",
    location: "US-Based Facility",
    duration: "Custom Specs",
    instructor: "Evermark Team",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cropped-Evermark-Logo-for-Digital-Use-2.png",
    image: "https://evermarkindustrial.com/wp-content/uploads/2026/06/Phenolic_Legend_Plates_2-scaled.jpg?v=sabok6x0z2",
    alt: "Close-up of precision-engraved phenolic legend plate",
    href: "/PhenolicPage"
  }, {
    date: "High Durability",
    title: "Stainless Steel Tags",
    description: "Laser-marked stainless steel for extreme conditions. Ideal for valve tags, asset tracking, and marine environments. Permanent, high-contrast, and fully compliant.",
    location: "US-Based Facility",
    duration: "Batch Orders",
    instructor: "Evermark Team",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cropped-Evermark-Logo-for-Digital-Use-2.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/Stainless_Steel_Plates-scaled.png",
    alt: "Laser-marked stainless steel industrial asset tags",
    href: "/ServicesPage"
  }, {
    date: "Safety Focused",
    title: "Arc Flash Labels",
    description: "Compliant arc flash and electrical safety labeling. We produce clear, durable warnings that meet current NEC and NFPA standards for your facility.",
    location: "US-Based Facility",
    duration: "Fast Turnaround",
    instructor: "Evermark Team",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cropped-Evermark-Logo-for-Digital-Use-2.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labeling.png",
    alt: "Industrial arc flash warning label on electrical equipment",
    href: "/ServicesPage"
  }, {
    date: "Precision Work",
    title: "Direct Part Marking",
    description: "Permanent laser marking directly onto metal components. Traceability for aerospace, medical, and automotive parts. Consistent, repeatable, and verified.",
    location: "US-Based Facility",
    duration: "Volume Production",
    instructor: "Evermark Team",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cropped-Evermark-Logo-for-Digital-Use-2.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/direct_part_marking.png",
    alt: "Laser marking process on a precision metal component",
    href: "/ServicesPage"
  }, {
    date: "Electrical ID",
    title: "Panel Schedules",
    description: "High-visibility phenolic panel schedules for commercial and industrial distribution boards. Custom layouts to match your specific breaker configurations.",
    location: "US-Based Facility",
    duration: "Standard Sizes",
    instructor: "Evermark Team",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cropped-Evermark-Logo-for-Digital-Use-2.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/panel_schedule_engraving.webp",
    alt: "Engraved phenolic electrical panel schedule",
    href: "/PhenolicPage"
  }, {
    date: "Asset Control",
    title: "Valve & Pipe Tags",
    description: "Durable identification for mechanical systems. Available in various colors for coding systems. Resistant to steam, oil, and industrial cleaning agents.",
    location: "US-Based Facility",
    duration: "Bulk Packs",
    instructor: "Evermark Team",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cropped-Evermark-Logo-for-Digital-Use-2.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/valve_tag_marking.png",
    alt: "Set of engraved phenolic valve tags",
    href: "/ServicesPage"
  }, {
    date: "Safety Protocol",
    title: "LOTO Tags",
    description: "Lockout/Tagout identification that meets OSHA requirements. Permanent engraving ensures safety instructions remain legible throughout the life of the equipment.",
    location: "US-Based Facility",
    duration: "Safety Specs",
    instructor: "Evermark Team",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cropped-Evermark-Logo-for-Digital-Use-2.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/loto_tag_engraving.png",
    alt: "Industrial lockout tagout phenolic tags",
    href: "/ServicesPage"
  }, {
    date: "Military Spec",
    title: "MIL-SPEC Nameplates",
    description: "Identification plates manufactured to rigorous military standards. Precise depth control and material verification for defense and aerospace applications.",
    location: "US-Based Facility",
    duration: "Certified Process",
    instructor: "Evermark Team",
    instructorImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cropped-Evermark-Logo-for-Digital-Use-2.png",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/milspec_nameplate.webp",
    alt: "Military specification engraved nameplate",
    href: "/PhenolicPage"
  }];
  return <section data-section-id="17" id="phenolicengravingcapabilities" className="bg-[#0F1B2D] text-[#E8E4DC] py-24 md:py-32 lg:py-40 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        <div ref={headerRef} className={`text-center mb-16 md:mb-20 lg:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs uppercase tracking-[0.3em] mb-6 text-[#E8E4DC]">
            Industrial Marking Services
          </p>
          <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-[#FFFFFF]">
            What Can Evermark Engrave in Phenolic?
          </h2>
          <div className="mx-auto mt-8 h-px w-16 bg-accent" />
        </div>

        
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-10 md:gap-12 lg:gap-16">
          {workshops.map((workshop, i) => <article key={i} data-index={i} className={`group flex flex-col overflow-hidden rounded-lg bg-[#1A2A40] border border-[#C9A227] shadow-lg hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 hover:bg-[#142132] ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
          transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
        }}>
              
              <div className="relative overflow-hidden aspect-[4/3] md:aspect-[5/4]">
                <img src={workshop.image} data-wvc-srcset={`${workshop.image}-wvc-srcset`} data-wvc-sizes={`${workshop.image}-wvc-sizes`} alt={workshop.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
              </div>

              
              <div className="flex-1 flex flex-col p-8 md:p-10 bg-transparent">
                <p className="font-default text-xs uppercase tracking-[0.25em] mb-5 text-[#C9A227]">
                  {workshop.date}
                </p>

                <h3 className="font-default font-light text-2xl md:text-3xl leading-tight text-[#E8E4DC] group-hover:text-[#C9A227] transition-colors duration-300 mb-4">
                  {workshop.title}
                </h3>

                <p className="font-default text-sm md:text-[15px] leading-relaxed text-[#E8E4DC]/80 mb-6 flex-1">
                  {workshop.description}
                </p>

                
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3 pt-6 border-t border-[#C9A227]/20 mb-6">
                  <div className="flex items-center gap-2 text-xs text-[#E8E4DC]/70">
                    <MapPin className="size-3.5 text-[#C9A227]" />
                    <span className="font-default">{workshop.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#E8E4DC]/70">
                    <Clock className="size-3.5 text-[#C9A227]" />
                    <span className="font-default">{workshop.duration}</span>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <SafeHydrate><Avatar className="size-7 border border-[#C9A227]/20 rounded-lg">
                      <AvatarImage src={workshop.instructorImage} alt={workshop.instructor} />
                      <AvatarFallback className="text-[10px] font-default bg-[#0F1B2D] text-[#E8E4DC] rounded-lg">
                        {workshop.instructor.split(" ").map(n => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <span className="font-default text-xs text-[#E8E4DC]/70">
                      {workshop.instructor}
                    </span>
                  </div>
                </div>

                
                {i > 4 && i !== 5}
              </div>
            </article>)}
        </div>

        
        
      </div>
    </section>;
}